/**
 * One function per platform endpoint. The only way a page talks to the API.
 *
 * Every function takes its path parameters first, its body or query next, and
 * `CallOptions` last — `signal` for reads (the hooks pass it), and
 * `idempotencyKey` for a write that a "try again" button must replay rather
 * than repeat.
 *
 * Each one names its route from `ROUTES`, so a path exists in exactly one
 * place and `routes.spec.ts` checks every one of them against the controller.
 */

import { api, apiResult, type ApiResult, type Query } from '../api';
import type { PageResult } from '../data/paged';

import { pathOf, ROUTES, type RouteDef, type RouteParams } from './routes';
import type {
  AuditActions,
  AuditFilters,
  ChainReport,
  CreatedMember,
  DirectoryCheck,
  CreateMemberInput,
  CreateTenantInput,
  DefaultBranding,
  DispatchQuery,
  DispatchStatus,
  EmailPreview,
  EmailPreviewInput,
  EmailTemplate,
  EmailTemplateInput,
  EmailTemplateKey,
  EmailTestInput,
  EmailTestResult,
  FileClaim,
  IdentityProvider,
  IdentityProviderInput,
  Insights,
  InvitationSent,
  JoinRequestList,
  JoinRequestQuery,
  LogoInput,
  LogoResult,
  MailBranding,
  MailDispatch,
  MailSettings,
  MailSettingsInput,
  MailTestResult,
  MemberList,
  MemberPermission,
  MemberQuery,
  ModelsCatalogue,
  ModelTenant,
  ModelTenantQuery,
  NumberedPage,
  OnboardedTenant,
  Operator,
  Page,
  PermissionChange,
  PermissionsCatalogue,
  PlatformAuditEntry,
  PlatformAuditFilters,
  PlatformAuditQuery,
  PlatformOverview,
  SessionTokens,
  SetRolePermissionsInput,
  SetRolePermissionsResult,
  SignInInput,
  RestoredMember,
  SignInRegistration,
  SlugCheck,
  Tenant,
  TenantActivity,
  TenantAuditEntry,
  TenantAuditQuery,
  TenantContentSummary,
  TenantListEntry,
  TenantModel,
  TenantModelInput,
  TenantModelPricingInput,
  ModelPriceList,
  RateCardRow,
  SetModelOfferingInput,
  FxRate,
  SetFxRateInput,
  RecordModelPriceInput,
  RestoreTestLog,
  RestoreTestInput,
  TenantNotificationsSummary,
  TenantOverview,
  TenantQuery,
  TenantRole,
  TenantRoleView,
  UpdatedMember,
  UpdateMemberInput,
  UpdateTenantInput,
  Vertical,
  TeamsCapabilityReport,
} from './types';

export interface CallOptions {
  signal?: AbortSignal;
  idempotencyKey?: string;
}

/* The two helpers every function below goes through. ------------------ */

function call<T, R extends RouteDef>(
  definition: R,
  params: RouteParams<R>,
  init: { body?: unknown; query?: Query } = {},
  options: CallOptions = {},
): Promise<T> {
  return api<T>(pathOf(definition, params), {
    method: definition.method,
    ...(init.body === undefined ? {} : { body: init.body }),
    ...(init.query === undefined ? {} : { query: init.query }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    ...(options.idempotencyKey === undefined ? {} : { idempotencyKey: options.idempotencyKey }),
  });
}

function callResult<T, R extends RouteDef>(
  definition: R,
  params: RouteParams<R>,
  init: { body?: unknown } = {},
  options: CallOptions = {},
): Promise<ApiResult<T>> {
  return apiResult<T>(pathOf(definition, params), {
    method: definition.method,
    ...(init.body === undefined ? {} : { body: init.body }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    ...(options.idempotencyKey === undefined ? {} : { idempotencyKey: options.idempotencyKey }),
  });
}

const none = {} as Record<string, never>;

/* Functions defined before `platform` so its callable members can carry them. */

function auditList(query: PlatformAuditQuery = {}, options: CallOptions = {}): Promise<Page<PlatformAuditEntry>> {
  return call<Page<PlatformAuditEntry>, typeof ROUTES.audit>(ROUTES.audit, none, { query: { ...query } }, options);
}

function tenantAuditList(
  id: string,
  query: TenantAuditQuery = {},
  options: CallOptions = {},
): Promise<Page<TenantAuditEntry>> {
  return call<Page<TenantAuditEntry>, typeof ROUTES.tenantAudit>(
    ROUTES.tenantAudit,
    { id },
    { query: { ...query } },
    options,
  );
}

function modelTenants(
  model: string,
  query: ModelTenantQuery = {},
  options: CallOptions = {},
): Promise<NumberedPage<ModelTenant>> {
  return call<NumberedPage<ModelTenant>, typeof ROUTES.modelTenants>(
    ROUTES.modelTenants,
    { model },
    { query: { ...query } },
    options,
  );
}

function tenantPage(query: TenantQuery = {}, options: CallOptions = {}): Promise<NumberedPage<TenantListEntry>> {
  // `limit` is the older spelling of `pageSize`; the server no longer reads it.
  const { limit, ...rest } = query;

  return call<NumberedPage<TenantListEntry>, typeof ROUTES.tenants>(
    ROUTES.tenants,
    none,
    { query: { ...rest, ...(rest.pageSize === undefined && limit !== undefined ? { pageSize: limit } : {}) } },
    options,
  );
}

function modelsCatalogue(options: CallOptions = {}): Promise<ModelsCatalogue> {
  return call<ModelsCatalogue, typeof ROUTES.models>(ROUTES.models, none, {}, options);
}

/** A `usePaged` fetcher over a `{ items, nextCursor }` endpoint. */
function cursorPages<T>(
  fetch: (cursor: string | undefined, signal: AbortSignal) => Promise<Page<T>>,
): (cursor: string | null, signal: AbortSignal) => Promise<PageResult<T, string>> {
  return async (cursor, signal) => {
    const page = await fetch(cursor ?? undefined, signal);

    return { items: page.items, next: page.nextCursor };
  };
}

const withCursor = <Q extends object>(query: Q, cursor: string | undefined): Q & { cursor?: string } =>
  cursor === undefined ? query : { ...query, cursor };

/* ------------------------------------------------------------------ */

export const platform = {
  auth: {
    /** No bearer, no refresh: a 401 here is "wrong email or password", shown as-is. Rate-limited to 10/min. */
    signIn: (input: SignInInput, options: CallOptions = {}) =>
      api<SessionTokens>(ROUTES.signIn.path, {
        method: 'POST',
        body: input,
        auth: false,
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      }),
    /** Moves the server-side watermark: every token this operator holds stops working. */
    signOut: (options: CallOptions = {}) => call<null, typeof ROUTES.signOut>(ROUTES.signOut, none, {}, options),
    me: (options: CallOptions = {}) => call<Operator, typeof ROUTES.me>(ROUTES.me, none, {}, options),
  },

  /** `GET /overview` — the three counts the old dashboard drew. Superseded by `insights` for the new one. */
  overview: (options: CallOptions = {}) =>
    call<PlatformOverview, typeof ROUTES.overview>(ROUTES.overview, none, {}, options),

  /** `GET /insights` — the dashboard. */
  insights: (options: CallOptions = {}) => call<Insights, typeof ROUTES.insights>(ROUTES.insights, none, {}, options),

  /** `GET /permissions` — both vocabularies and what each role holds. */
  permissions: (options: CallOptions = {}) =>
    call<PermissionsCatalogue, typeof ROUTES.permissions>(ROUTES.permissions, none, {}, options),

  /**
   * `platform.models(options)` — `GET /models`, the catalogue with adoption counts.
   * `platform.models.tenants(model, options)` — `GET /models/:model/tenants`, the drill-in.
   */
  models: Object.assign(modelsCatalogue, { tenants: modelTenants }),

  /** Alias of `platform.models.tenants`, the name the models page was written against. */
  modelTenants,

  verticals: (options: CallOptions = {}) =>
    call<Vertical[], typeof ROUTES.verticals>(ROUTES.verticals, none, {}, options),

  /**
   * The platform's default email look — what a المنشأة with no look of its own
   * sends with. Reading needs PLATFORM_TENANT_READ; writing is SUPERUSER-only
   * (PLATFORM_OPERATOR_MANAGE, enforced in the database too).
   */
  defaultBranding: {
    get: (options: CallOptions = {}) =>
      call<DefaultBranding, typeof ROUTES.defaultBranding>(ROUTES.defaultBranding, none, {}, options),
    /** A full replacement; `{}` clears it. A field outside the schema is a 400. Answers as GET does. */
    set: (input: MailBranding, options: CallOptions = {}) =>
      call<DefaultBranding, typeof ROUTES.setDefaultBranding>(
        ROUTES.setDefaultBranding,
        none,
        { body: input },
        options,
      ),
  },

  /**
   * The operator trail — what OPERATORS did. `PLATFORM_AUDIT_READ`.
   *
   * `platform.audit(query, options)` is `platform.audit.list`: one page,
   * `{ items, nextCursor }`, filtered by `action`, `actorId`, `actor`, `tenantId`, `from`, `to`.
   * Each row carries the `traceId` of the request that wrote it.
   */
  audit: Object.assign(auditList, {
    list: auditList,
    /** A `usePaged` fetcher over the cursor, with fixed filters. Put the filters in `usePaged`'s deps. */
    pages: (filters: PlatformAuditFilters = {}, limit = 50) =>
      cursorPages<PlatformAuditEntry>((cursor, signal) =>
        auditList(withCursor({ ...filters, limit }, cursor), { signal }),
      ),
    /** Every distinct action in the trail, A–Z — the action filter's options. */
    actions: (options: CallOptions = {}) =>
      call<AuditActions, typeof ROUTES.auditActions>(ROUTES.auditActions, none, {}, options),
    /** Walks the hash chain's links. Read with `chainVerdict` (`ok: null` is NOT a pass). Throttled 6/min → 429. */
    integrity: (options: CallOptions = {}) =>
      call<ChainReport, typeof ROUTES.auditIntegrity>(ROUTES.auditIntegrity, none, {}, options),
  }),

  tenants: {
    /**
     * Filtered, sorted and paged on the server (`q`, `status`, `vertical`, `sort`, `page`, `pageSize`),
     * with the `total` the pages are counted from. `limit` is read as `pageSize`.
     */
    page: tenantPage,
    /** The rows of one page, without the total — for a picker or a search box that never pages. */
    list: async (query: TenantQuery = {}, options: CallOptions = {}) => (await tenantPage(query, options)).items,
    /** Always 200 — an unavailable slug is an answer. Needs PLATFORM_TENANT_CREATE. POST still decides (409 `SLUG_TAKEN`). */
    slugCheck: (slug: string, options: CallOptions = {}) =>
      call<SlugCheck, typeof ROUTES.slugCheck>(ROUTES.slugCheck, none, { query: { slug } }, options),
    /**
     * Without `ownerPassword` the first admin is INVITED and mailed a link; read `invitation.delivery`.
     * `ownerPassword` is SUPERUSER-only (`canSetOpeningPassword`), else 403 `OPENING_PASSWORD_SUPERUSER_ONLY`.
     * 403 `OPERATOR_ADDRESS` when `ownerEmail` is an operator's. 409 `SLUG_TAKEN`.
     */
    create: (input: CreateTenantInput, options: CallOptions = {}) =>
      call<OnboardedTenant, typeof ROUTES.createTenant>(ROUTES.createTenant, none, { body: input }, options),
    get: (id: string, options: CallOptions = {}) =>
      call<Tenant, typeof ROUTES.tenant>(ROUTES.tenant, { id }, {}, options),
    /** `isActive: false` signs out every member on their next request. */
    update: (id: string, input: UpdateTenantInput, options: CallOptions = {}) =>
      call<Tenant, typeof ROUTES.updateTenant>(ROUTES.updateTenant, { id }, { body: input }, options),
    overview: (id: string, options: CallOptions = {}) =>
      call<TenantOverview, typeof ROUTES.tenantOverview>(ROUTES.tenantOverview, { id }, {}, options),
    setFrontendUrl: (id: string, frontendUrl: string | null, options: CallOptions = {}) =>
      call<Tenant, typeof ROUTES.setFrontendUrl>(ROUTES.setFrontendUrl, { id }, { body: { frontendUrl } }, options),
    /** Inline base64 or a `fileId` uploaded by `lib/upload.ts` (any other file id is 404); `{ bytes: null }` clears. */
    setLogo: (id: string, input: LogoInput, options: CallOptions = {}) =>
      call<LogoResult, typeof ROUTES.setLogo>(ROUTES.setLogo, { id }, { body: input }, options),
    /** The المنشأة's OWN trail (what its members did): one page, `{ items, nextCursor }`, with filters. */
    audit: tenantAuditList,
    /** Alias of `tenants.audit`. */
    auditList: tenantAuditList,
    /** A `usePaged` fetcher over the cursor, with fixed filters. Put `id` and the filters in `usePaged`'s deps. */
    auditPages: (id: string, limit = 50, filters: AuditFilters = {}) =>
      cursorPages<TenantAuditEntry>((cursor, signal) =>
        tenantAuditList(id, withCursor({ ...filters, limit }, cursor), { signal }),
      ),
    auditActions: (id: string, options: CallOptions = {}) =>
      call<AuditActions, typeof ROUTES.tenantAuditActions>(ROUTES.tenantAuditActions, { id }, {}, options),
    /** As `platform.audit.integrity`, for this المنشأة's trail. Throttled 6/min → 429. */
    auditIntegrity: (id: string, options: CallOptions = {}) =>
      call<ChainReport, typeof ROUTES.tenantAuditIntegrity>(ROUTES.tenantAuditIntegrity, { id }, {}, options),
    /** Weekly counts, oldest first, current week last. `weeks` 1–52, default 12. Counts only (ATA-36). */
    activity: (id: string, query: { weeks?: number } = {}, options: CallOptions = {}) =>
      call<TenantActivity, typeof ROUTES.tenantActivity>(
        ROUTES.tenantActivity,
        { id },
        { query: { ...query } },
        options,
      ),
    /**
     * Everything the المنشأة holds, as counts and sums — conversations, cases,
     * projects, documents, files, usage, members, notifications, ingestion.
     * No title, name, client or message, nothing per member (ATA-36).
     */
    contentSummary: (id: string, options: CallOptions = {}) =>
      call<TenantContentSummary, typeof ROUTES.tenantContentSummary>(ROUTES.tenantContentSummary, { id }, {}, options),
    /** Notifications sent (7 and 30 days), unread, and the month by kind code. Counts only (ATA-36). */
    notificationsSummary: (id: string, options: CallOptions = {}) =>
      call<TenantNotificationsSummary, typeof ROUTES.tenantNotificationsSummary>(
        ROUTES.tenantNotificationsSummary,
        { id },
        {},
        options,
      ),
  },

  members: {
    /** One page, filtered and sorted on the server, with the chip `counts` for the whole المنشأة. */
    list: (id: string, query: MemberQuery = {}, options: CallOptions = {}) =>
      call<MemberList, typeof ROUTES.members>(ROUTES.members, { id }, { query: { ...query } }, options),
    /**
     * Creates the account INVITED and sends the invitation, in one call; answers `invitation.expiresAt`.
     * 403 `OPERATOR_ADDRESS` for an operator's email.
     */
    create: (id: string, input: CreateMemberInput, options: CallOptions = {}) =>
      call<CreatedMember, typeof ROUTES.createMember>(ROUTES.createMember, { id }, { body: input }, options),
    /** Refused if it would leave the المنشأة with no active general admin. */
    update: (id: string, userId: string, input: UpdateMemberInput, options: CallOptions = {}) =>
      call<UpdatedMember, typeof ROUTES.updateMember>(ROUTES.updateMember, { id, userId }, { body: input }, options),
    /** 202 with `invitation.expiresAt`. Revokes any older link. Refused for a suspended member. */
    invite: (id: string, userId: string, options: CallOptions = {}) =>
      call<InvitationSent, typeof ROUTES.inviteMember>(ROUTES.inviteMember, { id, userId }, {}, options),
    /**
     * Put a removed member back. The counterpart to the firm's own
     * `DELETE /users/{id}`, which until now had none.
     *
     * It is here and not on the firm's own screens because the firm cannot do
     * it: its `GET /users` hides removed people, so an administrator who
     * removed the wrong colleague cannot see them to put them back. They come
     * back ACTIVE if they have a password and have used it, INVITED
     * otherwise, and old sessions stay dead.
     *
     * `restored: false` means they were already a member and nothing moved.
     */
    restore: (id: string, userId: string, options: CallOptions = {}) =>
      call<RestoredMember, typeof ROUTES.restoreMember>(ROUTES.restoreMember, { id, userId }, {}, options),
    permissions: (id: string, userId: string, options: CallOptions = {}) =>
      call<MemberPermission[], typeof ROUTES.memberPermissions>(ROUTES.memberPermissions, { id, userId }, {}, options),
    /**
     * `result.reason === 'no_change'` when it was already held — do not report an act that did not happen.
     * 403 `PERMISSION_OPENS_CONTENT` for CONVERSATION_REVIEW: check `platformMayGrant(code)` before offering it.
     */
    grant: (id: string, userId: string, permission: string, options: CallOptions = {}) =>
      callResult<PermissionChange, typeof ROUTES.grantPermission>(
        ROUTES.grantPermission,
        { id, userId, permission },
        {},
        options,
      ),
    revoke: (id: string, userId: string, permission: string, options: CallOptions = {}) =>
      callResult<PermissionChange, typeof ROUTES.revokePermission>(
        ROUTES.revokePermission,
        { id, userId, permission },
        {},
        options,
      ),
  },

  joinRequests: {
    list: (id: string, query: JoinRequestQuery = {}, options: CallOptions = {}) =>
      call<JoinRequestList, typeof ROUTES.joinRequests>(ROUTES.joinRequests, { id }, { query: { ...query } }, options),
    /** The role is required. The loser of two concurrent decisions gets a 404. 403 `OPERATOR_ADDRESS` for an operator's email. */
    approve: (id: string, userId: string, role: TenantRole, options: CallOptions = {}) =>
      call<null, typeof ROUTES.approveJoin>(ROUTES.approveJoin, { id, userId }, { body: { role } }, options),
    reject: (id: string, userId: string, options: CallOptions = {}) =>
      call<null, typeof ROUTES.rejectJoin>(ROUTES.rejectJoin, { id, userId }, {}, options),
  },

  roles: {
    list: (id: string, options: CallOptions = {}) =>
      call<TenantRoleView[], typeof ROUTES.roles>(ROUTES.roles, { id }, {}, options),
    /**
     * The whole list, not a delta — and it is stored (before migration 077 it
     * answered without writing). A system role is refused by the database;
     * 403 `PERMISSION_OPENS_CONTENT` if the list adds CONVERSATION_REVIEW.
     */
    setPermissions: (id: string, roleId: string, input: SetRolePermissionsInput, options: CallOptions = {}) =>
      call<SetRolePermissionsResult, typeof ROUTES.setRolePermissions>(
        ROUTES.setRolePermissions,
        { id, roleId },
        { body: input },
        options,
      ),
  },

  mail: {
    /** Null: the المنشأة uses the platform relay. */
    get: (id: string, options: CallOptions = {}) =>
      call<MailSettings | null, typeof ROUTES.mail>(ROUTES.mail, { id }, {}, options),
    /** Full replacement, except `password` — omit it to keep the stored one. */
    set: (id: string, input: MailSettingsInput, options: CallOptions = {}) =>
      call<MailSettings, typeof ROUTES.setMail>(ROUTES.setMail, { id }, { body: input }, options),
    /** Sends one real message. 200 either way — read `delivered`; 400 when there is nothing to test. */
    test: (id: string, to: string, options: CallOptions = {}) =>
      call<MailTestResult, typeof ROUTES.testMail>(ROUTES.testMail, { id }, { body: { to } }, options),
    dispatches: (id: string, query: DispatchQuery = {}, options: CallOptions = {}) =>
      call<Page<MailDispatch>, typeof ROUTES.mailDispatches>(
        ROUTES.mailDispatches,
        { id },
        { query: { ...query } },
        options,
      ),
    /** A page fetcher for `usePaged` over the keyset cursor. Put `status` in `usePaged`'s deps. */
    dispatchPages:
      (id: string, limit = 20, status?: DispatchStatus) =>
      async (cursor: string | null, signal: AbortSignal): Promise<PageResult<MailDispatch, string>> => {
        const page = await platform.mail.dispatches(
          id,
          { limit, ...(status === undefined ? {} : { status }), ...(cursor === null ? {} : { cursor }) },
          { signal },
        );

        return { items: page.items, next: page.nextCursor };
      },
  },

  emails: {
    templates: (id: string, options: CallOptions = {}) =>
      call<EmailTemplate[], typeof ROUTES.emailTemplates>(ROUTES.emailTemplates, { id }, {}, options),
    /** Answers the whole list again. An empty body clears the override. */
    setTemplate: (id: string, key: EmailTemplateKey, input: EmailTemplateInput, options: CallOptions = {}) =>
      call<EmailTemplate[], typeof ROUTES.setEmailTemplate>(
        ROUTES.setEmailTemplate,
        { id, key },
        { body: input },
        options,
      ),
    /** From the unsaved draft. `html` is a full document: render it in a sandboxed iframe. */
    preview: (id: string, key: EmailTemplateKey, input: EmailPreviewInput, options: CallOptions = {}) =>
      call<EmailPreview, typeof ROUTES.previewEmailTemplate>(
        ROUTES.previewEmailTemplate,
        { id, key },
        { body: input },
        options,
      ),
    test: (id: string, key: EmailTemplateKey, input: EmailTestInput, options: CallOptions = {}) =>
      call<EmailTestResult, typeof ROUTES.testEmailTemplate>(
        ROUTES.testEmailTemplate,
        { id, key },
        { body: input },
        options,
      ),
    /** Null: the platform's own look. */
    branding: (id: string, options: CallOptions = {}) =>
      call<MailBranding | null, typeof ROUTES.emailBranding>(ROUTES.emailBranding, { id }, {}, options),
    /** `{}` clears back to the platform's look. Unknown fields are refused. */
    setBranding: (id: string, input: MailBranding, options: CallOptions = {}) =>
      call<MailBranding | null, typeof ROUTES.setEmailBranding>(
        ROUTES.setEmailBranding,
        { id },
        { body: input },
        options,
      ),
  },

  teams: {
    /**
     * What this المنشأة's Microsoft application may do for the Teams sync.
     *
     * Separate from `identityProvider.directory`, which answers whether
     * members can sign in — a question needing no Graph permission at all.
     * The two share no scope, so a directory that signs members in perfectly
     * can be unable to read one channel.
     */
    capabilities: (id: string, options: CallOptions = {}) =>
      call<TeamsCapabilityReport, typeof ROUTES.teamsCapabilities>(ROUTES.teamsCapabilities, { id }, {}, options),
  },

  identityProvider: {
    /** Null: password sign-in only. */
    get: (id: string, options: CallOptions = {}) =>
      call<IdentityProvider | null, typeof ROUTES.identityProvider>(ROUTES.identityProvider, { id }, {}, options),
    /** Full replacement. Omit `clientSecret` to keep the stored one. */
    set: (id: string, input: IdentityProviderInput, options: CallOptions = {}) =>
      call<IdentityProvider, typeof ROUTES.setIdentityProvider>(
        ROUTES.setIdentityProvider,
        { id },
        { body: input },
        options,
      ),
    /** The redirect URI to register in Entra and the deployment's return-to rules. Answers even before a directory is saved. */
    registration: (id: string, options: CallOptions = {}) =>
      call<SignInRegistration, typeof ROUTES.signInRegistration>(ROUTES.signInRegistration, { id }, {}, options),
    /**
     * Spend the stored credentials against Microsoft and say what came back.
     *
     * `credentialsWork` is the sign-in answer; `problem` names which of the
     * hand-copied fields to go back to. `SECRET_INVALID` is overwhelmingly the
     * secret **id** pasted where the secret **value** belongs — Azure shows
     * them side by side and copies the id by default.
     *
     * `directory` is the name and the verified domains, bare and lowercase,
     * ready to be saved as `allowedEmailDomains`. It is null when the Graph
     * read was refused, which leaves `credentialsWork` true: sign-in needs no
     * Graph permission.
     */
    directory: (id: string, options: CallOptions = {}) =>
      call<DirectoryCheck, typeof ROUTES.identityProviderDirectory>(
        ROUTES.identityProviderDirectory,
        { id },
        {},
        options,
      ),
  },

  tenantModels: {
    /** Every catalogue model, in catalogue order, with `enabled` and `isDefault` for this المنشأة. */
    list: (id: string, options: CallOptions = {}) =>
      call<TenantModel[], typeof ROUTES.tenantModels>(ROUTES.tenantModels, { id }, {}, options),
    /**
     * Answers the model row. `reason === 'no_change'` when it was already so
     * (no audit row written). 409 `MODEL_IS_DEFAULT` switching the default off;
     * 409 with NO reason when the default changed mid-switch (`isModelSwitchRace`) —
     * reload the list and let them retry; 400 `MODEL_UNKNOWN` for an id outside the catalogue.
     */
    set: (id: string, model: string, input: TenantModelInput, options: CallOptions = {}) =>
      callResult<TenantModel, typeof ROUTES.setTenantModel>(
        ROUTES.setTenantModel,
        { id, model },
        { body: input },
        options,
      ),
    /**
     * §11 — what this المنشأة is billed per million tokens.
     *
     * Prices only: it cannot switch a model on, make it the default or rename
     * it, and a row it creates arrives switched **off** — pricing a model is
     * not approving it. `reason === 'no_change'` when every rate sent was
     * already so, and no audit row is written for it.
     */
    setPricing: (id: string, model: string, input: TenantModelPricingInput, options: CallOptions = {}) =>
      callResult<TenantModel, typeof ROUTES.setTenantModelPricing>(
        ROUTES.setTenantModelPricing,
        { id, model },
        { body: input },
        options,
      ),
  },

  /**
   * ATA-464 S15 — documented tests of the backups.
   *
   * `due` is the same verdict that raises `BACKUP_RESTORE_TEST_DUE` for every
   * منشأة's المشرف العام, so the panel and the firms' bells cannot disagree
   * about whether the platform is current. A failed test is listed and does
   * **not** restart the clock.
   */
  restoreTests: {
    list: (options: CallOptions = {}) =>
      call<RestoreTestLog, typeof ROUTES.restoreTests>(ROUTES.restoreTests, {}, {}, options),
    record: (input: RestoreTestInput, options: CallOptions = {}) =>
      call<RestoreTestLog, typeof ROUTES.recordRestoreTest>(ROUTES.recordRestoreTest, {}, { body: input }, options),
  },

  /**
   * §11 — the deployment's rate card: one price per model, for every منشأة.
   *
   * A firm's own rate still wins when it has one (`tenantModels.setPricing`),
   * because a منشأة with a negotiated contract pays its contract. Resolution
   * is **the firm's rate → this card → unpriced**.
   */
  modelRates: {
    card: (options: CallOptions = {}) =>
      call<RateCardRow[], typeof ROUTES.modelRateCard>(ROUTES.modelRateCard, {}, {}, options),
    /** Records a **new** rate from now; never edits the row in force. */
    record: (model: string, input: RecordModelPriceInput, options: CallOptions = {}) =>
      call<RateCardRow[], typeof ROUTES.recordModelPrice>(
        ROUTES.recordModelPrice,
        { model },
        { body: input },
        options,
      ),
    /**
     * Offers a model to every منشأة, or withdraws it from all of them.
     *
     * A withdrawn model is returned by no other endpoint, so this is the one
     * switch that decides what any firm can pick.
     */
    setOffering: (model: string, input: SetModelOfferingInput, options: CallOptions = {}) =>
      call<RateCardRow[], typeof ROUTES.setModelOffering>(
        ROUTES.setModelOffering,
        { model },
        { body: input },
        options,
      ),
  },

  /**
   * §11 — the ر.س/USD factor every published price is derived at.
   *
   * Setting it rewrites prices: the ر.س price of a published rate is the
   * dollar price times this factor, and letting the two drift apart is how a
   * deployment ends up billing at a rate that matches nothing.
   */
  fxRate: {
    read: (options: CallOptions = {}) => call<FxRate, typeof ROUTES.fxRate>(ROUTES.fxRate, {}, {}, options),
    set: (input: SetFxRateInput, options: CallOptions = {}) =>
      call<FxRate, typeof ROUTES.setFxRate>(ROUTES.setFxRate, {}, { body: input }, options),
  },

  /** What each provider publishes, to start from rather than retype. */
  modelPrices: {
    list: (options: CallOptions = {}) =>
      call<ModelPriceList, typeof ROUTES.modelPriceList>(ROUTES.modelPriceList, {}, {}, options),
  },

  /** Direct-to-bucket uploads. Use `lib/upload.ts`, which drives these four with retries and parts. */
  files: {
    presign: <T>(id: string, files: FileClaim[], options: CallOptions = {}) =>
      call<{ files: T[] }, typeof ROUTES.presignFiles>(ROUTES.presignFiles, { id }, { body: { files } }, options),
    parts: (id: string, fileId: string, from: number, to: number, options: CallOptions = {}) =>
      call<{ parts: { partNumber: number; url: string }[] }, typeof ROUTES.fileParts>(
        ROUTES.fileParts,
        { id, fileId },
        { body: { from, to } },
        options,
      ),
    complete: <T>(
      id: string,
      fileId: string,
      parts: { partNumber: number; etag: string }[] | undefined,
      options: CallOptions = {},
    ) =>
      call<T, typeof ROUTES.completeFile>(
        ROUTES.completeFile,
        { id, fileId },
        { body: parts === undefined ? {} : { parts } },
        options,
      ),
    abort: (id: string, fileId: string, options: CallOptions = {}) =>
      call<null, typeof ROUTES.abortFile>(ROUTES.abortFile, { id, fileId }, {}, options),
  },
} as const;
